/**
 * <ScoutFloatingButton>
 *
 * Bottom-right floating button (pulses gently to draw attention without
 * being annoying). On click, slides in a side panel containing
 * <ScoutDiscover variant="panel" />.
 *
 * Positioned slightly higher than the existing <FloatingChatbot> (the
 * FAQ bot) so the two don't overlap. Visible on every route except
 * /discover (where it would be redundant).
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import ScoutDiscover from './ScoutDiscover';

export default function ScoutFloatingButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Hide on /discover (the dedicated page already has Scout front-and-center).
  // Also hide on /admin/* so admin work isn't interrupted by the panel.
  const hide =
    location.pathname.startsWith('/discover') ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/checkout');

  useEffect(() => {
    // Close panel on route change.
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (hide) return null;

  return (
    <>
      {/* Button — positioned above the FAQ chatbot which sits at bottom-6 right-6 */}
      {!open && (
        <button
          type="button"
          aria-label="Ask Scout for product recommendations"
          data-testid="scout-floating-button"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 z-40 bg-cyan-400 text-black font-bold px-4 py-3 rounded-full shadow-lg shadow-cyan-400/20 hover:bg-cyan-300 transition-all flex items-center gap-2 group"
        >
          <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform" />
          <span className="text-sm">Ask Scout</span>
        </button>
      )}

      {/* Slide-in panel */}
      {open && (
        <>
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
            onClick={() => setOpen(false)}
            data-testid="scout-panel-overlay"
          />
          <div
            data-testid="scout-panel"
            className="fixed top-0 right-0 h-full w-full sm:w-[560px] bg-neutral-950 border-l border-white/10 z-50 shadow-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                <h2 className="text-white font-semibold">Ask Scout</h2>
                <span className="text-neutral-500 text-xs">Find the right AI tool</span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close Scout panel"
                data-testid="scout-panel-close"
                className="text-neutral-400 hover:text-white p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-6">
              <ScoutDiscover variant="panel" autoFocus />
            </div>
          </div>
        </>
      )}
    </>
  );
}
